"use client"

import { useEffect, useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Clock, Flag, MapPin, Navigation, Package, Zap } from "lucide-react"

import DriverScreen from "../components/DriverScreen.jsx"
import styles from "../css/driver.module.css"
import api from "../../../api"
import LiveMap from "../../../components/LiveMap"

export default function DriverRoutePlan() {
  const { jobId } = useParams()
  const navigate = useNavigate()

  const [route, setRoute] = useState(() => {
    const saved = sessionStorage.getItem("driverRouteMode")
    return saved === "shortest" ? "shortest" : "fastest"
  })

  const [job, setJob] = useState(null)
  const [routeData, setRouteData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [originCoords, setOriginCoords] = useState(null)
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos?.coords?.latitude
        const lng = pos?.coords?.longitude
        if (lat == null || lng == null) return
        // Keep accuracy: the server only pins the origin when it is good enough,
        // and In-Transit re-checks it before trusting the locked start.
        const accuracy = pos?.coords?.accuracy
        setOriginCoords({
          lat,
          lng,
          accuracy: accuracy != null ? Number(accuracy) : null,
        })
      },
      () => {
        // ignore; backend will fall back to the stop's pinned origin
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    )
  }, [])

  useEffect(() => {
    sessionStorage.setItem("driverRouteMode", route)
  }, [route])

  useEffect(() => {
    if (!jobId) return

    async function load() {
      try {
        setLoading(true)
        setError(null)

        // For the preview polyline we can optionally supply a live origin.
        // The stop's pinned origin/destination remain stored in the DB.
        const originParams = {
          originLat: originCoords?.lat,
          originLng: originCoords?.lng,
        }

        const [detailsRes, routeRes] = await Promise.all([
          api.get(`/api/driver/deliveries/${jobId}`),
          api.get(`/api/driver/deliveries/${jobId}/route`, {
            params: { mode: route, ...originParams },
          }),
        ])

        setJob(detailsRes?.data?.job || null)
        setRouteData(routeRes?.data || null)
      } catch (err) {
        console.error("[DriverRoutePlan] load:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [jobId, route, originCoords?.lat, originCoords?.lng])

  const currentJob = job

  const eta = useMemo(() => {
    const secondsRaw = routeData?.route?.durationSeconds
    const seconds = secondsRaw != null ? Number(secondsRaw) : NaN
    if (!Number.isFinite(seconds)) return null
    return Math.max(1, Math.round(seconds / 60))
  }, [routeData])

  const lineString = routeData?.route?.lineString
  const destination = routeData?.destination
    ? [routeData.destination.lng, routeData.destination.lat]
    : null
  const originPin = routeData?.origin ? [routeData.origin.lng, routeData.origin.lat] : null
  const mapCenter = destination || (lineString?.[0] ? lineString[0] : null)
  const startLabel = routeData?.originLabel || currentJob?.startPoint

  const items = Array.isArray(currentJob?.itemsDisplay) ? currentJob.itemsDisplay : []

  // Starting a delivery pins where the driver actually set off from. The server
  // only persists the origin when accuracy is good enough, so send it along
  // rather than posting an empty body and falling back to the branch address.
  const handleStart = async () => {
    if (!jobId || starting) return

    const lockedStart = {
      // Stamped so a stale entry from a previous stop can't be replayed onto this one.
      jobId: String(jobId),
      originLat: originCoords?.lat ?? null,
      originLng: originCoords?.lng ?? null,
      originAccuracy: originCoords?.accuracy ?? null,
      originLabel: startLabel || null,
      etaMinutes: eta,
      routeMode: route,
    }

    try {
      setStarting(true)
      setError(null)

      await api.post(`/api/driver/deliveries/${jobId}/start`, {
        originLat: lockedStart.originLat,
        originLng: lockedStart.originLng,
        originAccuracy: lockedStart.originAccuracy,
      })

      // In-Transit reads the locked start from navigation state, falling back to
      // sessionStorage so a mid-trip refresh doesn't lose the pinned origin.
      try {
        sessionStorage.setItem("driverLockedStart", JSON.stringify(lockedStart))
      } catch {
        // Storage can be unavailable (private mode / quota); navigation state still carries it.
      }

      navigate(`/driver/in-transit/${jobId}`, { state: lockedStart })
    } catch (err) {
      console.error("[DriverRoutePlan] start:", err)
      setError(err.message)
    } finally {
      setStarting(false)
    }
  }

  return (
    <DriverScreen>
      <h1 className={styles.screenTitle}>Review route</h1>

      {loading && <div className={styles.statusMessage}>Loading route…</div>}

      {!loading && error && <div className={styles.errorMessage}>{error}</div>}

      {!loading && !error && currentJob && (
        <>
          <div className={styles.panel}>
            <div className={styles.routeSummary}>
              <div className={styles.routeLeg}>
                <span className={styles.legIcon} aria-hidden="true">
                  <Navigation size={15} />
                </span>
                <div>
                  <div className={styles.routeLabel}>Start point</div>
                  <div className={styles.routeValue}>{startLabel || "—"}</div>
                </div>
              </div>

              <div className={styles.routeLeg}>
                <span className={`${styles.legIcon} ${styles.legIconEnd}`} aria-hidden="true">
                  <Flag size={15} />
                </span>
                <div>
                  <div className={styles.routeLabel}>Destination</div>
                  <div className={styles.routeValue}>
                    {currentJob.destination?.line1 || "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.amountRow}>
              <span className={styles.amountLabel}>Estimated drive</span>
              <span className={styles.amountValue}>{eta ?? "—"} min</span>
            </div>
          </div>

          <div className={styles.panel}>
            <div className={styles.field}>
              <span className={styles.fieldLabel} id="route-mode-label">
                Route preference
              </span>
              <div
                className={styles.segmentGroup}
                role="radiogroup"
                aria-labelledby="route-mode-label"
              >
                <div className={styles.segment}>
                  <input
                    type="radio"
                    id="route-fastest"
                    name="routeMode"
                    checked={route === "fastest"}
                    onChange={() => setRoute("fastest")}
                  />
                  <label className={styles.segmentLabel} htmlFor="route-fastest">
                    <Zap size={16} aria-hidden="true" />
                    Fastest
                  </label>
                </div>
                <div className={styles.segment}>
                  <input
                    type="radio"
                    id="route-shortest"
                    name="routeMode"
                    checked={route === "shortest"}
                    onChange={() => setRoute("shortest")}
                  />
                  <label className={styles.segmentLabel} htmlFor="route-shortest">
                    <MapPin size={16} aria-hidden="true" />
                    Shortest
                  </label>
                </div>
              </div>
            </div>

            <LiveMap
              center={mapCenter}
              destination={destination}
              driverPosition={originPin}
              autoFocusOnDriver={true}
              followDriver={true}
              routeLineString={Array.isArray(lineString) ? lineString : null}
              height={320}
              zoom={13}
            />
          </div>

          <div className={styles.panel}>
            <h2 className={styles.sectionLabel}>Delivery details</h2>

            <div className={styles.detailRow}>
              <Package size={16} aria-hidden="true" />
              <span>{items.length ? items.join(", ") : "No items listed"}</span>
            </div>

            <div className={styles.detailRow}>
              <Clock size={16} aria-hidden="true" />
              <span>{currentJob.timeWindow || "—"}</span>
            </div>
          </div>

          <button
            type="button"
            className={styles.primaryBtn}
            disabled={loading || !!error || !currentJob || starting}
            onClick={handleStart}
          >
            <Navigation size={18} aria-hidden="true" />
            {starting ? "Starting…" : "Start delivery"}
          </button>
        </>
      )}
    </DriverScreen>
  )
}
